import { useState, useEffect, useContext } from "react";
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import AppContext from "../../../context";

import Loading from "../../components/Loading";
import Meta from "../../components/Meta";
import CreateBooking from "./CreateBooking.action";

import ukMoment from '../../../utils/ukTime';

import Data from '../../../repository';

import styles from '../../../styles/Booking.module.scss';

function Confirm() {
	const params = useParams();
	const navigate = useNavigate();
	const context = useContext(AppContext);

	const [searchParams, setSearchParams] = useSearchParams();
	const [error, setError] = useState("");
	const [confirmed, setConfirmed] = useState(false);
	const [waitlistLoading, setWaitlistLoading] = useState(false);
	const [waitlistMessage, setWaitlistMessage] = useState("");

	const [loading, setLoading] = useState(true);
	const [session, setSession] = useState();

	const start = searchParams.get('start');
	const end = searchParams.get('end');
	const shareLink = searchParams.get('shareLink');

	useEffect(async () => {
		if(!start || !end) exit();

		try {
			const session = await fetchSession(params.studio, start, end, shareLink);
			if(session.isAlreadyBooked) exit();
			setSession(session)
			setLoading(false);
		} catch(error) {
			exit();
		}
	}, []);

	const exit = () => {
		navigate(`/dashboard/book/${params.studio}`);
	}

	const fetchSession = async (studio, start, end, shareLink) => {
		const result = await Data.fetchSession(studio, start, end, shareLink);
		return result.data;
	}

	const toggleWaitlist = async () => {
		setError("");
		setWaitlistMessage("");
		setWaitlistLoading(true);
		const payload = {
			type: "session",
			studioId: session.studio._id,
			start: session.start,
			end: session.end
		};

		try {
			if(session.waitlisted) {
				await Data.leaveWaitlist(payload);
				setSession(current => ({ ...current, waitlisted: false }));
				setWaitlistMessage("You've left the waitlist.");
			} else {
				await Data.joinWaitlist(payload);
				setSession(current => ({ ...current, waitlisted: true }));
				setWaitlistMessage("You're on the waitlist. We'll email you if this session becomes available.");
			}
		} catch(error) {
			setError(error.response?.data?.message || "Couldn't update the waitlist.");
		} finally {
			setWaitlistLoading(false);
		}
	}

	if(loading) return <Loading button={false} />;

	let available = true;
	if(session.booked && !session.shareLink) available = false;
	if(session.withheld) available = false;
	if(session.past) available = false;

	const requiresActivation = context.domain?.requireActivation && !context.user?.admin && !context.user?.isActive;
	const isHybrid = context.domain?.businessType === 'hybrid' || context.domain?.businessType === 'hybrid-new';
	const openGymTokenCost = Number(context.domain?.openGymTokenCost ?? 1);
	const availableOpenGymTokens = Number((context.domain?.businessType === 'hybrid-new' ? context.user?.openGymBalance : context.user?.walletBalance) || 0);

	return (
		<>
			<Meta title="Confirm Booking" />

			<div className={styles.booking}>
				{confirmed && <p className={styles.success}>Your booking has been confirmed!</p>}
				{error && <p className={styles.error}>{error}</p>}
				{waitlistMessage && <p className={styles.success}>{waitlistMessage}</p>}
				{requiresActivation && !confirmed && (
					<p className={styles.error}>Your account is awaiting activation. Please contact the business to complete your activation.</p>
				)}

				{(isHybrid ? availableOpenGymTokens < openGymTokenCost : context.user.balance < 1) && !confirmed && <p className={styles.error}>You don't have enough tokens to create this booking. You can purchase tokens <Link to="/dashboard/tokens">here</Link>.</p>}

				<img src={require('../../../images/gym.svg').default} alt="" />

				{((session.booked || session.withheld || session.past) || session.shareLink) &&
					<div className={styles.message}>
						{session.past ?
							<p>It's too late to book this session.</p>
						: session.shareLink ?
							<p>This is a shared session.</p>
						:
							<p>This session is unavailable.</p>
						}
					</div>
				}

				<div className={styles.details}>
					<h1>{ukMoment(session.start).format("ddd Do MMMM YYYY")} at {ukMoment(session.start).format("HH:mm a")}</h1>
					<h1>{session.studio.name}</h1>
					{Number.isInteger(session.remainingCapacity) && Number.isInteger(session.capacity) && <p>{session.remainingCapacity} of {session.capacity} spaces remaining</p>}
					{isHybrid && <p>{openGymTokenCost} token{openGymTokenCost === 1 ? '' : 's'} required</p>}
				</div>

				{(!confirmed && (isHybrid ? availableOpenGymTokens >= openGymTokenCost : context.user.balance > 0) && !requiresActivation) &&
					<div className={styles.actions}>
						<button className="btn btnEmpty" onClick={() => { navigate(-1) }}>Cancel</button>
						{available && <CreateBooking session={session} shareLink={shareLink} setError={setError} setConfirmed={setConfirmed} />}
					</div>
				}

				{!confirmed && !available && !session.past && !session.shareLink && context.user?.paidExtrasActive &&
					<div className={styles.actions}>
						<button className="btn btnEmpty" onClick={() => { navigate(-1) }}>Back</button>
						<button className="btn btnPrimary" onClick={toggleWaitlist} disabled={waitlistLoading}>
							{waitlistLoading ? "Updating..." : session.waitlisted ? "Leave the waitlist" : "Join the waitlist"}
						</button>
					</div>
				}
			</div>
		</>
	)
}

export default Confirm;
